import { useState, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { callResultsApi, callCampaignsApi } from "@/api";
import { format } from "date-fns";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Phone,
  Filter,
} from "lucide-react";

const outcomeLabels: Record<string, string> = {
  PENDING: "処理中",
  CALLING: "発信中",
  CALLBACK_SCHEDULED: "CB予約",
  NOT_INTERESTED: "興味なし",
  WRONG_PERSON: "別人",
  NO_ANSWER: "不応答",
  BUSY: "話中",
  VOICEMAIL: "留守電",
  BOT_DETECTED: "AI受付",
  TRANSFER_TIMEOUT: "転送TO",
  TRANSFER_REFUSED: "取次拒否",
  FAILED: "失敗",
  CANCELLED: "キャンセル",
};

const outcomeColors: Record<string, string> = {
  CALLBACK_SCHEDULED: "bg-green-100 text-green-800",
  NOT_INTERESTED: "bg-red-100 text-red-800",
  WRONG_PERSON: "bg-orange-100 text-orange-800",
  NO_ANSWER: "bg-slate-100 text-slate-600",
  BUSY: "bg-yellow-100 text-yellow-800",
  VOICEMAIL: "bg-blue-100 text-blue-700",
  BOT_DETECTED: "bg-purple-100 text-purple-700",
  TRANSFER_TIMEOUT: "bg-orange-100 text-orange-700",
  TRANSFER_REFUSED: "bg-red-100 text-red-700",
  FAILED: "bg-red-200 text-red-900",
  CANCELLED: "bg-slate-100 text-slate-500",
  PENDING: "bg-slate-50 text-slate-400",
  CALLING: "bg-blue-50 text-blue-600",
};

export default function CallTranscripts() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [outcome, setOutcome] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: campaigns } = useQuery({
    queryKey: ["call-campaigns-list"],
    queryFn: () => callCampaignsApi.list(),
  });

  const params: Record<string, string> = { page: String(page), limit: "20" };
  if (search) params.search = search;
  if (outcome) params.outcome = outcome;
  if (campaignId) params.campaignId = campaignId;

  const { data: results, isLoading } = useQuery({
    queryKey: ["call-results", params],
    queryFn: () => callResultsApi.list(params),
  });

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Phone size={24} className="text-purple-600" />
          <h1 className="text-2xl font-bold">架電トランスクリプト</h1>
        </div>
        <p className="text-sm text-slate-500">
          全キャンペーンの架電結果を横断検索
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Filter size={14} />
          フィルター
        </div>
        <div className="flex flex-wrap gap-3">
          {/* Text search */}
          <div className="flex gap-1">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="トランスクリプト内を検索..."
              className="border rounded-md px-3 py-1.5 text-sm w-64"
            />
            <button
              onClick={handleSearch}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700"
            >
              <Search size={14} />
            </button>
          </div>

          {/* Outcome filter */}
          <select
            value={outcome}
            onChange={(e) => { setOutcome(e.target.value); setPage(1); }}
            className="border rounded-md px-3 py-1.5 text-sm"
          >
            <option value="">全結果</option>
            {Object.entries(outcomeLabels).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>

          {/* Campaign filter */}
          <select
            value={campaignId}
            onChange={(e) => { setCampaignId(e.target.value); setPage(1); }}
            className="border rounded-md px-3 py-1.5 text-sm"
          >
            <option value="">全キャンペーン</option>
            {campaigns?.data.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          {(search || outcome || campaignId) && (
            <button
              onClick={() => {
                setSearch(""); setSearchInput(""); setOutcome(""); setCampaignId(""); setPage(1);
              }}
              className="text-sm text-slate-500 hover:text-slate-700 underline"
            >
              リセット
            </button>
          )}
        </div>
      </div>

      {/* Results table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-400">読み込み中...</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left px-4 py-2 font-medium text-slate-600">コンタクト</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">会社</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">キャンペーン</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">結果</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">時間</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">ターン</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">日時</th>
                </tr>
              </thead>
              <tbody>
                {results?.data.map((r) => (
                  <Fragment key={r.id}>
                    <tr
                      className={`border-b hover:bg-slate-50 cursor-pointer ${
                        expanded === r.id ? "bg-slate-50" : ""
                      }`}
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    >
                      <td className="px-4 py-2">
                        {r.contact ? (
                          <span
                            className="text-blue-600 hover:underline"
                            onClick={(e) => { e.stopPropagation(); navigate(`/contacts/${r.contactId}`); }}
                          >
                            {[r.contact.lastName, r.contact.firstName].filter(Boolean).join(" ") || r.contact.email}
                          </span>
                        ) : r.contactId.slice(0, 8)}
                      </td>
                      <td className="px-4 py-2 text-slate-600">{r.contact?.company || "-"}</td>
                      <td className="px-4 py-2 text-slate-600">
                        {r.callCampaign ? (
                          <span
                            className="text-blue-600 hover:underline"
                            onClick={(e) => { e.stopPropagation(); navigate(`/call-campaigns/${r.callCampaignId}`); }}
                          >
                            {r.callCampaign.name}
                          </span>
                        ) : "-"}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${outcomeColors[r.outcome] || "bg-slate-50"}`}>
                          {outcomeLabels[r.outcome] || r.outcome}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-slate-600">
                        {r.duration > 0 ? `${r.duration}秒` : "-"}
                      </td>
                      <td className="px-4 py-2 text-slate-600">{r.turns || "-"}</td>
                      <td className="px-4 py-2 text-slate-500">
                        {format(new Date(r.updatedAt), "MM/dd HH:mm")}
                      </td>
                    </tr>

                    {/* Expanded transcript */}
                    {expanded === r.id && (
                      <tr key={`${r.id}-transcript`}>
                        <td colSpan={7} className="px-4 py-3 bg-slate-50">
                          {r.transcript && r.transcript.length > 0 ? (
                            <div className="space-y-2 max-w-2xl">
                              <p className="text-xs font-medium text-slate-500 mb-2">
                                トランスクリプト ({r.transcript.length}ターン)
                              </p>
                              {r.transcript.map((t, i) => (
                                <div key={i} className={`flex gap-2 ${t.role === "assistant" ? "" : "justify-end"}`}>
                                  <div className={`px-3 py-1.5 rounded-lg text-sm max-w-md ${
                                    t.role === "assistant"
                                      ? "bg-purple-100 text-purple-900"
                                      : "bg-white border text-slate-700"
                                  }`}>
                                    <span className="text-xs text-slate-400 mr-1">
                                      {t.role === "assistant" ? "AI" : "相手"}:
                                    </span>
                                    {highlightSearch(t.text, search)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400">トランスクリプトなし</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {results?.data.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                      {search ? `「${search}」に一致する結果がありません` : "結果がありません"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {results && results.totalPages > 1 && (
              <div className="p-4 border-t flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  {results.total}件中 {(page - 1) * 20 + 1}-{Math.min(page * 20, results.total)}件
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-slate-50"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="px-3 py-1 text-sm">
                    {page} / {results.totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(results.totalPages, p + 1))}
                    disabled={page >= results.totalPages}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-slate-50"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function highlightSearch(text: string, search: string): React.ReactNode {
  if (!search) return text;
  const idx = text.toLowerCase().indexOf(search.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 rounded px-0.5">{text.slice(idx, idx + search.length)}</mark>
      {text.slice(idx + search.length)}
    </>
  );
}
